// ================================================================================================
// React
import { Component } from "react";

// lodash
import _ from "lodash";

// Material UI components
import IconButton from 'material-ui/IconButton';
import FlatButton from 'material-ui/FlatButton';
import TextField from 'material-ui/TextField';
import IconMenu from 'material-ui/IconMenu';
import MenuItem from 'material-ui/MenuItem';
import UndoIcon from 'material-ui/svg-icons/content/undo';
import { Toolbar, ToolbarGroup, ToolbarTitle } from 'material-ui/Toolbar';

import { grey600 } from 'material-ui/styles/colors';

// ICONS
import NewFolderIcon from 'material-ui/svg-icons/file/create-new-folder';
import MoreVertIcon from 'material-ui/svg-icons/navigation/more-vert';
import EditIcon from 'material-ui/svg-icons/editor/mode-edit';
import FolderIcon from 'material-ui/svg-icons/file/folder';
import DeleteIcon from 'material-ui/svg-icons/action/delete';

// Yodiwo Libs
import { buildPathFromStructure } from "../Primitive/TreeStructureHelper";
import { IComponentSizeProp } from '@Primitive/Interfaces';
import { isDefined, isFunction } from '@Utils/Micro';
import { DialogConf, ExDialog } from '@Primitive/ExDialog';
import { ReorderTreeStructure } from '@Primitive/ReorderTreeStructure';
import { FolderItemType, FolderStructure } from '@Primitive/TreeStructure';
import { isAnimationEnabled } from '@Utils/Globals';
import { BoardTheming } from "./BoardTheming";
// ================================================================================================

export interface EditFolderName {
    openEditDialog: boolean;
    id: number;
    label: string;
}

export interface PagesFolderStructureProps extends IComponentSizeProp {
    onCloseRequest: () => void;
    onSaveRequest: (structure: FolderStructure[], sumItems: number) => void;
    Structure: FolderStructure[];
    SumItems: number;
    UndeletePage: boolean;
    ShowFolderOptions: boolean;
}

export interface PagesFolderStructureState {
    dialogInfo?: DialogConf;
    editFolderName: EditFolderName;
    reorderStructure: FolderStructure[];
    sumItems: number;
}
// ================================================================================================

export class PagesFolderStructure extends Component<PagesFolderStructureProps, PagesFolderStructureState> {
    constructor(props: PagesFolderStructureProps) {
        super(props);

        this.state = {
            dialogInfo: null,
            editFolderName: { openEditDialog: false, id: null, label: null },
            reorderStructure: this.updateFolderStructure(null, _.cloneDeep(props.Structure)),
            sumItems: props.SumItems
        };
    }
    // --------------------------------------------------------------------------------------------
    private showGenericDialog(dialogInfo: DialogConf) { this.setState({ dialogInfo: dialogInfo }); }
    // --------------------------------------------------------------------------------------------
    private closeGenericDialog = () => { this.setState({ dialogInfo: undefined }); }
    // --------------------------------------------------------------------------------------------
    private setFolderName(id: number, label: string) {
        this.setState({
            editFolderName: {
                openEditDialog: true,
                id: id,
                label: label
            }
        });
    }
    // --------------------------------------------------------------------------------------------
    private renameDashboardFolder(structure: FolderStructure[]): FolderStructure[] {
        structure.forEach((element) => {
            if (element.type == FolderItemType.Folder && element._id == this.state.editFolderName.id) {
                element.primaryText = this.state.editFolderName.label;
            } else if (element.type == FolderItemType.Folder && element.children.length > 0) {
                element.children = this.renameDashboardFolder(element.children);
            }
        });

        return structure;
    }
    // --------------------------------------------------------------------------------------------
    private deleteDashboardFolder(parentChildren: FolderStructure[], folder: FolderStructure) {
        // Move the children to the parent or to the root (null parent)
        // Rename paths
        _.forEach(folder.children, (c) => {
            if (c.path != "/") {
                // Remove the last part of the paths
                const split = c.path.split("/");
                split.splice(split.length - 2, 1);
                c.path = split.join("/");
            }
        });

        // move the childress to the parent
        if (isDefined(parentChildren)) {
            // Move the childress one level down
            parentChildren.push(...folder.children);

            // remove the folder from the parent
            _.remove(parentChildren, (p) => p._id == folder._id);
        }

        // Force the UI to update based on the delete
        this.setState((prevState: PagesFolderStructureState) => {
            prevState.reorderStructure = this.updateFolderStructure(null, prevState.reorderStructure);
            return prevState;
        });
    }
    // --------------------------------------------------------------------------------------------
    private undeleteDashboardPage(ItemIde: number, path: string, action: boolean) {
        // Force the UI to update based on the undelete
        this.setState((prevState: PagesFolderStructureState) => {
            let structure = _.cloneDeep(prevState.reorderStructure);
            let split = _.split(path, "/");

            _.forEach(structure, (c) => {
                if (c._id == ItemIde)
                    c.undeletePage = action;
                else if (c.type == FolderItemType.Folder && _.indexOf(split, c.primaryText) > 0) {
                    c.children = this.undeleteFolderDashboardPage(c.children, ItemIde, split, action);
                }
            });
            prevState.reorderStructure = this.updateFolderStructure(null, structure);
            return prevState;
        });
    }
    // --------------------------------------------------------------------------------------------
    private undeleteFolderDashboardPage(structure: FolderStructure[], ItemIde: number, split, action) {
        _.forEach(structure, (c) => {
            if (c._id == ItemIde)
                c.undeletePage = action;
            else if (c.type == FolderItemType.Folder && _.indexOf(split, c.primaryText) > 0) {
                c.children = this.undeleteFolderDashboardPage(c.children, ItemIde, split, action);
            }
        });
        return structure;
    }
    // --------------------------------------------------------------------------------------------
    private onTreeDataChange = (tree) => {
        if (!this.props.UndeletePage) {
            const jsonTreeStruct = buildPathFromStructure(tree.toJS(), "/");
            this.setState((prevState: PagesFolderStructureState) => {
                prevState.reorderStructure = this.updateFolderStructure(null, _.cloneDeep(jsonTreeStruct));
                return prevState;
            });
        }
    }
    // --------------------------------------------------------------------------------------------
    private updateFolderStructure(parent: FolderStructure, structure: FolderStructure[]) {
        const disableRipple = !isAnimationEnabled();

        _.forEach(structure, (s) => {
            if (isDefined(parent))
                s.parentId = parent._id;

            if (s.type == FolderItemType.Folder) {
                // Update children
                s.children = _.uniq(s.children);

                // Update right icon
                s.rightIcon = this.props.ShowFolderOptions ? <IconMenu
                    key={"Reorder-Structure-Icon-Menu-" + s._id}
                    iconButtonElement={
                        <IconButton
                            touch={true}
                            disableFocusRipple={disableRipple}
                            disableTouchRipple={disableRipple}
                        >
                            <MoreVertIcon color={grey600} />
                        </IconButton>
                    }
                >
                    <MenuItem
                        key={"Reorder-Structure-Icon-Menu-Settings-Item-" + s._id}
                        leftIcon={<EditIcon />}
                        onClick={() => this.setFolderName(s._id, s.primaryText)}
                        disableFocusRipple={disableRipple}
                        disableTouchRipple={disableRipple}
                    >Rename</MenuItem>
                    <MenuItem
                        key={"Reorder-Structure-Icon-Menu-Delete-Item-" + s._id}
                        leftIcon={<DeleteIcon />}
                        onClick={() => this.deleteDashboardFolder(parent != null ? parent.children : structure, s)}
                        disableFocusRipple={disableRipple}
                        disableTouchRipple={disableRipple}
                    >Delete</MenuItem>
                </IconMenu> : null;

                // Move to the childrens
                if (isDefined(s.children) && s.children.length > 0)
                    this.updateFolderStructure(s, s.children);
            } else if (s.type == FolderItemType.Dashboard) {
                s.rightIcon = (this.props.UndeletePage) ? (s.undeletePage ?
                    <IconButton
                        touch={true}
                        onClick={() => { this.undeleteDashboardPage(s._id, s.path, false); }}
                        disableFocusRipple={disableRipple}
                        disableTouchRipple={disableRipple}>
                        <DeleteIcon />
                    </IconButton> :
                    <IconButton
                        touch={true}
                        onClick={() => { this.undeleteDashboardPage(s._id, s.path, true); }}
                        disableFocusRipple={disableRipple}
                        disableTouchRipple={disableRipple}>
                        <UndoIcon />
                    </IconButton>) : null;
            }
        });

        return structure;
    }
    // --------------------------------------------------------------------------------------------
    private getToolbarListGroup(): JSX.Element[] {
        const disableRipple = !isAnimationEnabled();
        let toolbarList = [];
        let actionModalTitle: string = this.props.UndeletePage ? "Restore Pages" : "Rearrange Pages";

        if (!this.props.UndeletePage)
            toolbarList.push(
                <IconButton
                    key="reorder-modal-add-folder"
                    tooltip="Add new Folder"
                    touch={true}
                    onClick={() => {
                        this.setState({
                            editFolderName:
                            {
                                openEditDialog: true,
                                id: null,
                                label: "New folder"
                            }
                        });
                    }}
                    disableFocusRipple={disableRipple}
                    disableTouchRipple={disableRipple}
                >
                    <NewFolderIcon color="white" />
                </IconButton>);

        toolbarList.push([
            <FlatButton
                key="PagesFolderStructureCancel"
                label="Cancel"
                style={{ margin: 0 }}
                secondary={true}
                onClick={() => {
                    if (isFunction(this.props.onCloseRequest))
                        this.props.onCloseRequest();
                }}
            />,
            <FlatButton
                key="PagesFolderStructureSave"
                label="Save"
                style={{ margin: 0 }}
                primary={true}
                onClick={() => {
                    this.showGenericDialog({
                        actions: [
                            <FlatButton
                                label="No"
                                secondary={true}
                                onClick={this.closeGenericDialog}
                            />,
                            <FlatButton
                                label="Yes"
                                primary={true}
                                onClick={() => {
                                    this.closeGenericDialog();

                                    if (isFunction(this.props.onSaveRequest))
                                        this.props.onSaveRequest(this.state.reorderStructure, this.state.sumItems);
                                }}
                            />
                        ],
                        title: actionModalTitle,
                        // todo: get somehow common style for body container (we can use ThemeContext)
                        body: <div style={{
                            padding: 24, color: "initial"
                        }}>
                            Are you sure you want to save this dashboard's structure? <br />
                            All the empty folders will automatically removed.
                        </div>
                    });
                }
                }
            />
        ]);

        return toolbarList;
    }
    // --------------------------------------------------------------------------------------------
    render() {
        const theme = BoardTheming.GetActiveTheme();
        const toolbarHeight = theme.dialog.height;

        // these action are for edit folder name dialog
        const setFolderNameDialogActions = [
            <FlatButton
                label="No"
                secondary={true}
                onClick={() =>
                    // Close the dialog
                    this.setState({
                        editFolderName:
                        {
                            openEditDialog: false,
                            id: -1,
                            label: ""
                        }
                    })}
            />,
            <FlatButton
                label="Yes"
                primary={true}
                onClick={() => {
                    // Close the dialog
                    if (this.state.editFolderName.id != null) {
                        let newStructure: FolderStructure[] = _.cloneDeep(this.state.reorderStructure);
                        newStructure = this.renameDashboardFolder(newStructure);
                        newStructure = buildPathFromStructure(newStructure, "/");

                        this.setState((prevState: PagesFolderStructureState) => {
                            prevState.editFolderName.openEditDialog = false;
                            prevState.editFolderName.id = -1;
                            prevState.editFolderName.label = "";
                            prevState.reorderStructure = newStructure;
                            return prevState;
                        });
                    }
                    else {
                        let newStructure: FolderStructure[] = _.cloneDeep(this.state.reorderStructure);
                        newStructure.push({
                            _id: this.state.sumItems,
                            path: "/",
                            parentId: 0,
                            type: FolderItemType.Folder,
                            children: [],
                            primaryText: this.state.editFolderName.label,
                            leftIcon: (<FolderIcon />)
                        });
                        newStructure = buildPathFromStructure(newStructure, "/");

                        this.setState((prevState: PagesFolderStructureState) => {
                            prevState.editFolderName = {
                                openEditDialog: false,
                                id: -1,
                                label: ""
                            };
                            prevState.reorderStructure = newStructure;
                            prevState.sumItems += 1;
                            return prevState;
                        });
                    }
                }}
            />
        ];

        // todo: get somehow common style for toolbar (we can use ThemeContext)
        return <div style={{ height: this.props.height, width: this.props.width }}>
            <Toolbar key="title-toolbar" style={{
                boxShadow: "rgba(0, 0, 0, 0.16) 0px 3px 10px, rgba(0, 0, 0, 0.23) 0px 3px 10px",
                position: "relative", color: "white", backgroundColor: "#134459", zIndex: 1000,
                height: toolbarHeight
            }}>
                <ToolbarGroup>
                    <ToolbarTitle style={{ color: "white" }} key="title-toolbar-title" text={this.props.UndeletePage ? "Restore Pages" : "Rearrange Pages"} />
                </ToolbarGroup>
                <ToolbarGroup style={{ color: "white" }} lastChild={true}>
                    <div style={{
                        boxSizing: 'border-box',
                        textAlign: 'right',
                        display: "table",
                        marginRight: 10
                    }}>
                        {
                            _.map(this.getToolbarListGroup(),
                                (x, i) => <div
                                    key={i}
                                    style={{
                                        height: toolbarHeight,
                                        display: "table-cell",
                                        verticalAlign: "middle"
                                    }}
                                >
                                    {x}
                                </div>)
                        }
                    </div>
                </ToolbarGroup>
            </Toolbar>

            <div style={{ height: this.props.height - toolbarHeight, overflowY: "auto" }}>
                <ReorderTreeStructure
                    key="ReorderTreeStructure"
                    ReorderStructure={this.state.reorderStructure}
                    onTreeDataChange={this.onTreeDataChange}
                />
            </div>

            <ExDialog
                key="set-folder-name-dialog"
                Enable={this.state.editFolderName.openEditDialog}
                Conf={{
                    actions: setFolderNameDialogActions,
                    title: "Set Folder Name",
                    body: <div style={{ padding: 24, color: 'initial' }}>
                        <TextField
                            ref={(r) => {
                                if (isDefined(r)) {
                                    // defined means that the component is mounted
                                    // so, we focus on text field
                                    setTimeout(() => {
                                        r.focus();
                                    }, 100)
                                }
                            }}
                            floatingLabelText="Folder's name"
                            id="text-field-controlled"
                            value={this.state.editFolderName.label}
                            onChange={(event) => {
                                let newValue = (event.target as any).value
                                this.setState((prevState) => {
                                    prevState.editFolderName.label = newValue;
                                    return prevState;
                                });
                            }}
                        />
                    </div>
                }}
            />

            <ExDialog
                key="generic-dialog"
                Enable={isDefined(this.state.dialogInfo)}
                Conf={this.state.dialogInfo}
            />
        </div>
    }
    // --------------------------------------------------------------------------------------------
}